import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import type {
  ChargeCustomerCheckoutOrderRequest,
  ChargeCustomerCheckoutOrderResult,
  CreateCustomerCheckoutOrderRequest,
  CreateCustomerCheckoutOrderResult,
  PaymentCheckoutConfig,
  StripeCompleteSessionRequest,
  StripeEmbeddedSession,
  StripeSessionCompletion,
} from '../../core/models/payment.models';
import type {
  CheckoutDeliveryAddress,
  PreviewCheckoutCouponResult,
  ShippoAddressValidationResult,
  ShippoCartLine,
  ShippoSenderAddress,
  ShippoShipmentResult,
} from './checkout.model';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  private readonly http = inject(HttpClient);
  private readonly shippoApiUrl = `${environment.apiUrl}Shippo`;
  private readonly customerCheckoutApiUrl = `${environment.apiUrl}CustomerCheckout`;

  getSenderAddress(): Observable<ShippoSenderAddress> {
    return this.http.get<ShippoSenderAddress>(`${this.shippoApiUrl}/sender`);
  }

  validateAddress(address: CheckoutDeliveryAddress): Observable<ShippoAddressValidationResult> {
    return this.http.post<ShippoAddressValidationResult>(`${this.shippoApiUrl}/address/validate`, {
      ...address,
      validate: true,
    });
  }

  getShippingRates(
    address: CheckoutDeliveryAddress,
    cartLines: ShippoCartLine[],
  ): Observable<ShippoShipmentResult> {
    return this.http.post<ShippoShipmentResult>(`${this.shippoApiUrl}/shipments`, {
      addressTo: {
        ...address,
        validate: true,
      },
      cartLines,
      async: false,
    });
  }

  previewCoupon(couponCode: string, subtotal: number): Observable<PreviewCheckoutCouponResult> {
    return this.http.post<PreviewCheckoutCouponResult>(`${this.customerCheckoutApiUrl}/coupon/preview`, {
      couponCode,
      subtotal,
    });
  }

  getPaymentConfig(): Observable<PaymentCheckoutConfig> {
    return this.http.get<PaymentCheckoutConfig>(`${this.customerCheckoutApiUrl}/payment/config`);
  }

  createCustomerOrder(
    request: CreateCustomerCheckoutOrderRequest,
  ): Observable<CreateCustomerCheckoutOrderResult> {
    return this.http.post<CreateCustomerCheckoutOrderResult>(
      `${this.customerCheckoutApiUrl}/orders`,
      request,
    );
  }

  chargeOrder(
    request: ChargeCustomerCheckoutOrderRequest,
  ): Observable<ChargeCustomerCheckoutOrderResult> {
    return this.http.post<ChargeCustomerCheckoutOrderResult>(
      `${this.customerCheckoutApiUrl}/payment/charge`,
      request,
    );
  }

  createEmbeddedPaymentSession(orderId: number): Observable<StripeEmbeddedSession> {
    return this.http.post<StripeEmbeddedSession>(
      `${this.customerCheckoutApiUrl}/payment/embedded-session`,
      {
        orderId,
        returnOrigin: typeof window !== 'undefined' ? window.location.origin : undefined,
      },
    );
  }

  completeEmbeddedSession(sessionId: string): Observable<StripeSessionCompletion> {
    return this.http.post<StripeSessionCompletion>(
      `${this.customerCheckoutApiUrl}/payment/complete-session`,
      { sessionId } satisfies StripeCompleteSessionRequest,
    );
  }
}
